import HostingVolumeReport from '../HostingVolumeReport/HostingVolumeReport';
import React, {useEffect, useRef, useState} from 'react';
import {t} from 'i18next';
import AurTabMenu, {AurTab} from '../../common/aur-tab-menu/AurTabMenu';
import CumulativeVolumeReportChart from '../HostingVolumeReport/component/CumulativeVolumeReportChart';
import {Typography} from '@mui/material';
import HostingUserSummaryReportHeader from '../HostingUserSummaryReport/HostingUserSummaryReportHeaders';
import {useDispatch, useSelector} from 'react-redux';
import {setDialogReportName} from '../../../redux/slices/reportsDialogSlice';
import {
  fetchHostingVolumeSummaryDetails,
  getExportReportXlsx,
  getHostingVolumeReportById,
} from '../../../api/ReportingManagement';
import {
  downloadResponseAsXLSX,
  getFilenameFromHeaders,
} from '../../../utils/utils';
import {failedToLoadStatusCodes} from '../../../constants/constants';
import {RootState} from '../../../redux/store';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_REPORT_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

interface CustodianData {
  total: number;
  loaded: number;
  released: number;
}

interface TotalVolume {
  loaded: string | number;
  released: string | number;
}

const HostingVolumeReportPage: React.FC = () => {
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  const [selectedRowData, setSelectedRowData] = useState<CustodianData | null>(
    null
  );
  const [apiData, setApiData] = useState<CustodianData | null>(null);

  const [totalVolume, setTotalVolume] = useState<TotalVolume>({
    loaded: 0,
    released: 0,
  });
  const [isFailedToLoadContent, setIsFailedToLoadContent] =
    useState<boolean>(false);

  const [isEventTracked, setIsEventTracked] = useState(false);

  const refreshDates = useSelector(
    (state: RootState) => state.reports.refreshDates
  );

  dispatch(
    setDialogReportName(t('reports.projectOverview.tabs.tabHostinVolume'))
  );

  const {clientId, projectId, workspaceId} = useSelector(
    (state: RootState) => state.reports
  );
  const [isFetchHostingVolumeSummary, setIsHostingVolumeSummary] =
    useState(false);

  const apiResponseTime = useRef('');

  const fetchSummaryDetails = async () => {
    setIsHostingVolumeSummary(true);
    setIsFailedToLoadContent(false);
    try {
      const result = await fetchHostingVolumeSummaryDetails(
        clientId,
        projectId,
        workspaceId
      );
      setTotalVolume((prev) => ({
        ...prev,
        loaded: result.data.data.loaded,
        released: result.data.data.released,
      }));
    } catch (e: any) {
      if (failedToLoadStatusCodes.includes(e.status))
        setIsFailedToLoadContent(true);
    } finally {
      setIsHostingVolumeSummary(false);
    }
  };

  const fetchHostingVolumeData = async () => {
    setIsHostingVolumeSummary(true);
    setIsFailedToLoadContent(false);
    try {
      const response = await getHostingVolumeReportById(
        clientId,
        projectId,
        workspaceId
      );
      apiResponseTime.current = response.data.responseTime;
      const {loaded, released} = response.data.data;

      // Update totalVolume state
      setTotalVolume((prev) => ({
        ...prev,
        loaded,
        released,
      }));

      // Update apiData and selectedRowData state
      const fetchedData = {
        total: 0,
        loaded,
        released,
      };
      setApiData(fetchedData);
      setSelectedRowData(fetchedData);
    } catch (e: any) {
      if (failedToLoadStatusCodes.includes(e.status)) {
        setIsFailedToLoadContent(true);
      }
      apiResponseTime.current = e.responseTime;
    } finally {
      setIsHostingVolumeSummary(false);
      !isEventTracked &&
        trackGAEvent({
          eventName: GA_EVENT_NAMES.PAGE_VIEW,
          page: GA_DATA_MODE_AUTO,
          user: GA_DATA_MODE_AUTO,
          report: {
            name: t('gaTracking.reports.tabHostinVolume'),
            type: GA_REPORT_TYPE.STANDARD,
            load_time: apiResponseTime.current,
          },
        });
      setIsEventTracked(true);
    }
  };

  // Single useEffect with dependency check
  useEffect(() => {
    if (clientId && projectId && workspaceId) {
      fetchHostingVolumeData();
    }
  }, [clientId, projectId, workspaceId]);

  const tabs: AurTab[] = [
    {
      header: {
        text: t('reports.hostingVolume.tab.byInventory'),
      },
      content: (
        <>
          <CumulativeVolumeReportChart
            custodianData={[
              {
                emailCompressed: selectedRowData?.loaded?.toString() || '',
                externallyProcessed:
                  selectedRowData?.released?.toString() || '',
                receivedDate: '',
                custodianName: t(
                  'reports.hostingVolume.cumulativeReport.tabDocs'
                ),
                id: '',
                subTotal: selectedRowData?.total?.toString() || '',
              },
            ]}
            totalCumulativeReceivedVolume={{
              email: totalVolume?.loaded?.toString() || '',
              externallyProcessed: totalVolume?.released?.toString() || '',
            }}
            isFailedToLoadContent={isFailedToLoadContent}
            onRefresh={fetchSummaryDetails}
            containerHeight={48}
          />
          <HostingVolumeReport
            apiData={apiData}
            setSelectedRowData={setSelectedRowData}
          />
        </>
      ),
      index: 0,
    },
  ];

  const handleDownloadSummaryReportXlsx = async () => {
    try {
      const response = await getExportReportXlsx(
        clientId as string,
        projectId as string,
        workspaceId as string,
        '/hosting-volume'
      );
      const fileName =
        getFilenameFromHeaders(response.headers['content-disposition']) ??
        `${workspaceId}_${Date.now()}`;
      downloadResponseAsXLSX(response, fileName, false);
    } catch (error: any) {
      console.error(error?.message);
    }
  };
  const onClickTabHeaderButton = () => {
    handleDownloadSummaryReportXlsx();
    trackGAEvent({
      eventName: GA_EVENT_NAMES.EXPORT,
      click: {
        name: t('gaTracking.reports.exportOption.xlsx'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.reports.exportReport'),
      },
      file_extension: GA_FILE_EXTENTIONS.XLSX,
    });
  };
  return (
    <>
      <HostingUserSummaryReportHeader
        refreshDate={refreshDates.hostingVolume}
        openFrom={t('reports.projectOverview.tabs.tabHostinVolume')}
      />
      <div className="intake-audit-report-detail">
        <div className="high-level-production-report-section">
          <Typography className="high-level-production-report-title typo-text subhead1_size">
            {t('reports.hostingVolume.label')}
          </Typography>
        </div>
        <div className="processing-summary-content-hosting-volume">
          <Typography className="processing-summary-description">
            {t('reports.hostingVolume.description')}
          </Typography>
        </div>
        <AurTabMenu
          headerClasses="typo-text subhead4_size intake-audit-report-tab-menu-header"
          tabs={tabs}
          width={'100%'}
          tabMenuHeader={t('reports.hostingVolume.tab.title')}
          tabHeaderClassName="intake-audit-report-tabs-heading-text"
          tabHeaderMainContainerClassName="intake-audit-report-tab-menu-main-container"
          tabHeaderSubContainerClassName="intake-audit-report-tab-sub-main-container"
          showTabHeaderButton
          tabHeaderDropDownButton={t(
            'reports.hostingVolume.exportReportButton'
          )}
          dropDownButtonHintText={t('reports.hostingVolume.exportButtonHint')}
          onClickTabHeaderButton={onClickTabHeaderButton}
        />
      </div>
      <AurLoadingIndicator
        overlay={true}
        isOpen={isFetchHostingVolumeSummary}
      />
    </>
  );
};

export default HostingVolumeReportPage;
